/**
 * Page-lifetime completion reconciler for Home Chat. It deliberately lives
 * above the conditionally mounted panel so an open Waypoint tab can still
 * surface a finished background turn after the user closes the chat pane.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';

import { acknowledgeCompletion, listSessions } from './api';
import {
  completionNoticeFromTransition,
  completionSoundIdFromTransition,
  HOME_CHAT_LLM_RESULT_SOUND_EVENT,
  llmResultEventSoundIdsFromSession,
  prepareInAppCompletionSound,
  tryInAppCompletionSound,
} from './completionNotifications';
import type { CompletionNotice } from './completionNotifications';

export const HOME_CHAT_COMPLETION_SOUND_STORAGE_KEY = 'home-chat-completion-sound-enabled';
export const HOME_CHAT_COMPLETION_SOUND_EVENT = 'home-chat:completion-sound-preference';

type Props = {
  activeSessionId: string | null;
  onView: (sessionId: string) => void;
};

function soundPreference(): boolean {
  try {
    return window.localStorage.getItem(HOME_CHAT_COMPLETION_SOUND_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

const HomeChatCompletionReconciler: React.FC<Props> = ({ activeSessionId, onView }) => {
  const deliveredCompletionIdsRef = useRef<Set<string>>(new Set());
  const deliveredSoundCompletionIdsRef = useRef<Set<string>>(new Set());
  // This is the single main-Home-Chat dispatcher ownership point for durable
  // actionable LLM results. It lives above the conditional chat panel, so it
  // covers both selected and background conversations but is never mounted by
  // WorkflowChatCompanion or a direct workflow page.
  const deliveredLlmResultEventIdsRef = useRef<Set<string>>(new Set());
  const initializedBaselineRef = useRef(false);
  const activeSessionIdRef = useRef(activeSessionId);
  const [notice, setNotice] = useState<CompletionNotice | null>(null);

  useEffect(() => {
    activeSessionIdRef.current = activeSessionId;
  }, [activeSessionId]);

  useEffect(() => {
    const refreshSoundPreference = () => {
      const enabled = soundPreference();
      // This event is synchronously dispatched by the speaker-button click,
      // so it is still an explicit browser user gesture.
      if (enabled) void prepareInAppCompletionSound();
    };
    const prepareOnNextUserGesture = () => {
      if (soundPreference()) void prepareInAppCompletionSound();
    };
    window.addEventListener(HOME_CHAT_COMPLETION_SOUND_EVENT, refreshSoundPreference);
    window.addEventListener('storage', refreshSoundPreference);
    // A persisted setting survives a reload but an AudioContext does not.
    // The next normal interaction (for example Send) re-unlocks it before a
    // background answer can arrive; this never prompts or plays a sound.
    document.addEventListener('pointerdown', prepareOnNextUserGesture);
    document.addEventListener('keydown', prepareOnNextUserGesture);
    return () => {
      window.removeEventListener(HOME_CHAT_COMPLETION_SOUND_EVENT, refreshSoundPreference);
      window.removeEventListener('storage', refreshSoundPreference);
      document.removeEventListener('pointerdown', prepareOnNextUserGesture);
      document.removeEventListener('keydown', prepareOnNextUserGesture);
    };
  }, []);

  useEffect(() => {
    // Response-time events are emitted only by an opted-in main Home Chat
    // panel after it receives a durable server event id. Keep delivery here,
    // alongside poll delivery, so one page-lifetime set fences both paths.
    const deliverImmediately = (event: Event) => {
      const eventId = (event as CustomEvent<{ eventId?: unknown }>).detail?.eventId;
      if (typeof eventId !== 'string' || !eventId || deliveredLlmResultEventIdsRef.current.has(eventId)) return;
      deliveredLlmResultEventIdsRef.current.add(eventId);
      if (soundPreference()) void tryInAppCompletionSound();
    };
    window.addEventListener(HOME_CHAT_LLM_RESULT_SOUND_EVENT, deliverImmediately);
    return () => window.removeEventListener(HOME_CHAT_LLM_RESULT_SOUND_EVENT, deliverImmediately);
  }, []);

  const reconcile = useCallback(async () => {
    try {
      const summary = await listSessions();
      if (!initializedBaselineRef.current) {
        // A reload/restart must not replay every durable, unread Ready marker
        // as a new completion. Keep those markers server-side until viewed;
        // only a completion that first appears after this baseline may alert.
        for (const session of summary.sessions || []) {
          const completion = session.completion;
          if (completion?.completion_id) {
            deliveredCompletionIdsRef.current.add(completion.completion_id);
            deliveredSoundCompletionIdsRef.current.add(completion.completion_id);
          }
          for (const event of session.llm_result_events || []) {
            deliveredLlmResultEventIdsRef.current.add(event.event_id);
          }
        }
        initializedBaselineRef.current = true;
        return;
      }
      for (const session of summary.sessions || []) {
        // Do not derive sound from cards, timestamps, or polling state. The
        // stable event ID is created at the server's active-branch commit
        // point, then returned only through authorized Home Chat reads.
        for (const eventId of llmResultEventSoundIdsFromSession(
          session,
          deliveredLlmResultEventIdsRef.current,
        )) {
          deliveredLlmResultEventIdsRef.current.add(eventId);
          if (soundPreference()) void tryInAppCompletionSound();
        }
        const soundId = completionSoundIdFromTransition(
          session,
          deliveredSoundCompletionIdsRef.current,
        );
        if (soundId) {
          deliveredSoundCompletionIdsRef.current.add(soundId);
          // Sound is independent from the selected-session visual-toast rule:
          // a real new answer in the open conversation receives this explicit
          // opt-in cue as well as a background answer does.
          if (soundPreference()) void tryInAppCompletionSound();
        }
        const next = completionNoticeFromTransition(
          undefined,
          session,
          activeSessionIdRef.current,
          deliveredCompletionIdsRef.current,
        );
        if (!next) {
          // The currently open conversation is already in view. Do not replay
          // it as a background event if the user subsequently navigates away.
          if (session.session_id === activeSessionIdRef.current) {
            const completion = session.completion;
            if (completion?.completion_id) deliveredCompletionIdsRef.current.add(completion.completion_id);
          }
          continue;
        }
        deliveredCompletionIdsRef.current.add(next.completionId);
        setNotice(next);
        // Sound is independently enabled; the durable Ready marker and this
        // in-app notice remain available if autoplay prevents the chime.
        // Read durable local preference at the exact completion boundary.
        // A speaker click and this poll can land in the same React batch; a
        // state closure from before that click would otherwise permanently
        // skip this one completion even though the setting is enabled.
        break;
      }
    } catch {
      // A failed client poll is not a terminal operation state. Keep the
      // server-visible Ready marker for the next successful reconciliation.
    }
  }, []);

  useEffect(() => {
    void reconcile();
    const poll = window.setInterval(() => { void reconcile(); }, 3000);
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') void reconcile();
    };
    window.addEventListener('focus', refreshWhenVisible);
    document.addEventListener('visibilitychange', refreshWhenVisible);
    return () => {
      window.clearInterval(poll);
      window.removeEventListener('focus', refreshWhenVisible);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
    };
  }, [reconcile]);

  const handleView = useCallback(async () => {
    if (!notice) return;
    onView(notice.sessionId);
    setNotice(null);
    try {
      await acknowledgeCompletion(notice.sessionId, notice.completionId);
    } catch {
      // View remains useful even if acknowledgement races; the server retains
      // Ready so it can be reconciled and acknowledged later.
    }
  }, [notice, onView]);

  return (
    <Snackbar
      open={notice !== null}
      onClose={() => setNotice(null)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
    >
      {notice ? (
        <Alert
          severity="success"
          variant="filled"
          action={<Button color="inherit" size="small" onClick={() => { void handleView(); }}>View</Button>}
        >
          Your answer is ready.
        </Alert>
      ) : undefined}
    </Snackbar>
  );
};

export default HomeChatCompletionReconciler;
